/**
 * NOT-604/605 — the single source of truth for "is this line a membership".
 *
 * A membership can be TYPED `MEMBERSHIP` or carry a `membership` tax category,
 * and every path that treats memberships differently has to classify them
 * identically: they are exempt from tax, out of the document-discount base, out
 * of a bundle's taxable base, and out of the deposit base. Answering the
 * question twice is how one path came to tax, discount or apportion money
 * another path had already set aside.
 *
 * WHY IT LIVES IN A PACKAGE. Operations owned this rule and the frontend
 * preview had its own type-only spelling of it, so a service-shaped line
 * carrying `taxCategory: 'membership'` was inside the preview's bundle base and
 * outside the server's — the operator saw a per-line tax split the save would
 * not store. `@notifi/shared-types` is already a dependency of both apps and
 * already ships runtime modules this way: a `.cjs` + `.d.cts` pair on a package
 * subpath, read straight from `src` with no build step, so operations' jest and
 * Nest build and the frontend's vite and vitest can all load it. That is the
 * only reason for the file extension.
 *
 * The two apps can no longer disagree about this, because there is nothing left
 * to disagree with.
 */
function isMembershipLine(line) {
  return (
    isMembershipSetupFeeLine(line) ||
    String(line?.type ?? "")
      .trim()
      .toUpperCase() === "MEMBERSHIP" ||
    String(line?.taxCategory ?? "")
      .trim()
      .toLowerCase() === "membership"
  );
}

function isMembershipSetupFeeLine(line) {
  return (
    String(line?.type ?? "")
      .trim()
      .toUpperCase() === "MEMBERSHIP_SETUP"
  );
}

function isMembershipPurchaseLine(line) {
  return isMembershipLine(line) && !isMembershipSetupFeeLine(line);
}

function membershipSetupFee(line) {
  const value =
    line?.membershipPurchaseSnapshot?.planSnapshot?.setupFee ??
    line?.membershipSetupFee;
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0
    ? Math.round(amount * 100) / 100
    : 0;
}

/** Derived charges cannot outlive, or be selected independently of, their sale. */
function withMembershipSetupFees(lines) {
  return lines
    .filter((line) => !isMembershipSetupFeeLine(line))
    .flatMap((line) => {
      const fee = isMembershipPurchaseLine(line) ? membershipSetupFee(line) : 0;
      if (!fee) return [line];
      const identity =
        line.rowId || line._rowId || line.lineKey || line.lineItemId;
      const planId =
        line.membershipPurchaseSnapshot?.planId ?? line.membershipPlanId;
      return [
        line,
        {
          ...line,
          rowId: `${identity}:setup`,
          ...(line._rowId ? { _rowId: `${identity}:setup` } : {}),
          lineKey: `${identity}:setup`,
          parentLineKey: null,
          type: "MEMBERSHIP_SETUP",
          name: `One-time setup fee — ${line.name || "Membership"}`,
          description:
            "Charged once at enrollment. Not part of the recurring membership price.",
          quantity: 1,
          basePrice: fee,
          manualPriceOverride: null,
          lineSubtotal: fee,
          lineTotal: fee,
          lineListTotal: fee,
          discountAmount: 0,
          discountPercent: 0,
          membershipDiscountAmount: 0,
          memberPrice: null,
          memberDiscountPercent: null,
          costBasis: 0,
          laborRate: 0,
          estimatedHours: 0,
          priceStrategy: "FLAT",
          taxable: false,
          taxCategory: "membership",
          membershipSetupFeeFor: identity,
          membershipSetupFeePlanId: planId,
          membershipLineKind: "setup",
          membershipPurchaseSnapshot: undefined,
          membershipPlanSummary: undefined,
          membershipPlanId: undefined,
          membershipBillingOptionId: undefined,
          membershipCustomerChoosesBillingOption: undefined,
          membershipSetupFee: undefined,
          equipmentIds: [],
          membershipDeliveredServiceIds: [],
        },
      ];
    });
}

module.exports = {
  isMembershipLine,
  isMembershipSetupFeeLine,
  isMembershipPurchaseLine,
  membershipSetupFee,
  withMembershipSetupFees,
};
